import { Box, TableCell } from "@mui/material";
import { useEffect, useState } from "react";
import { Contact, PaginatedListContact } from "../../services/swagger";
import { ContactService } from "../../services/api";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../organisms/DataView";
import ItemCard from "../organisms/ItemCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import CreateContactDialog from "../organisms/Contact/CreateContactDialog";
import { useBusinessStore } from "../../stores/Dummy";
import EditContactDialog from "../organisms/Contact/EditContactDialog";
import useRefresh from "../../hooks/useRefresh";

export default function ContactsPage() {
  const { t } = useTranslation("Contact");
  const { _id } = useParams();
  const navigate = useNavigate();

  const [createDialog, setCreateDialog] = useState(false);
  const [openedContact, setOpenedContact] = useState<Contact>();

  const [query, setQuery] = useState<QueryParameters>();

  const { mutate: loadContact } = useMutation({
    mutationFn: async () => {
      if (!_id) return;
      const res = await ContactService.getContact({ id: _id });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setOpenedContact(data);
    },
  });

  const loadContacts = async (): Promise<PaginatedListContact | null> => {
    if (!query) return null;
    const res = await ContactService.getContactList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const {
    data: contacts,
    isPlaceholderData: isLoading,
    refetch,
  } = useQuery({
    queryFn: loadContacts,
    queryKey: ["contacts", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToContact = (contact: Contact) => {
    navigate({
      pathname: `/m/business/contacts/${contact._id}`,
      search: window.location.search,
    });
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleCloseContact = () => {
    if (_id) navigate(-1);
    setOpenedContact(undefined);
  };

  useEffect(() => {
    if (_id) loadContact();
  }, [_id]);

  useRefresh(refetch);

  return (
    <Box height={1}>
      <CreateContactDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />
      <EditContactDialog contact={openedContact} onClose={handleCloseContact} />
      <DataView
        name={t("Contacts")}
        loading={isLoading || !contacts}
        onItemClick={(item) => navigateToContact(item)}
        headers={[
          {
            field: "firstName",
            name: t("Name"),
            type: "other",
            renderRowCell: (row: Contact) => (
              <TableCell
                sx={{
                  minWidth: 100,
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[row.title, row.firstName, row.middleName, row.lastName]
                  .filter((e) => e)
                  .join(" ")}
              </TableCell>
            ),
          },
          { field: "email", name: t("Email"), type: "string" },
          {
            field: "phones",
            name: t("Phones"),
            type: "other",
            renderRowCell: (row: Contact) => (
              <TableCell
                sx={{
                  minWidth: 100,
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.phones.join(",")}
              </TableCell>
            ),
          },
          {
            field: "company.name",
            name: t("Company"),
            type: "other",
            renderRowCell: (row: Contact) => (
              <TableCell
                sx={{
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.company?.name}
              </TableCell>
            ),
          },
          { field: "position", name: t("Position"), type: "string" },
          { field: "extRef", name: t("ExtRef"), type: "string" },
        ]}
        onChange={handleDataTableChange}
        items={contacts?.docs || []}
        count={contacts?.count || 0}
        renderItem={(contact) => (
          <ItemCard
            contact={contact}
            onClick={() => navigateToContact(contact)}
          />
        )}
        pagination
        queryFunction={ContactService.getContactListQuery}
        button={{
          text: t("NewContact"),
          onClick: () => setCreateDialog(true),
        }}
      />
    </Box>
  );
}
